import type { TrashEntry } from '@/api/types'

export type { TrashEntry }

/**
 * The trashable types this fork ships.
 *
 * Anything outside this set falls through to the unnamed rendering rather than
 * being given a translation it will never use. That is no longer about the
 * schema, which since M8 lists nothing this fork cannot show -- it is about
 * entries written before the builder and automation apps were deleted, which
 * can still be sitting in `core_trashentry`.
 */
export type TrashItemType =
  'workspace' | 'application' | 'table' | 'field' | 'row' | 'rows' | 'view' | 'widget'

export type TrashKey =
  | 'trash.itemApplication'
  | 'trash.itemField'
  | 'trash.itemRow'
  | 'trash.itemRows'
  | 'trash.itemTable'
  | 'trash.itemUnknown'
  | 'trash.itemView'
  | 'trash.itemWidget'
  | 'trash.itemWorkspace'

export interface TrashDescription {
  key: TrashKey
  values: Record<string, string | number>
}

type Describer = (entry: TrashEntry) => TrashDescription

/**
 * What each kind of trashed thing is called on screen.
 *
 * `satisfies` rather than an annotation, so adding a type to the union above is
 * a compile error until it has a description. The distinction the entries
 * themselves make and this has to keep is between `row` and `rows`: a batch
 * delete is one entry carrying `names`, and describing it as a single row would
 * misreport how much a restore is about to bring back.
 */
const DESCRIBERS = {
  workspace: (entry) => ({ key: 'trash.itemWorkspace', values: { name: entry.name } }),
  application: (entry) => ({
    key: 'trash.itemApplication',
    values: { name: entry.name },
  }),
  table: (entry) => ({
    key: 'trash.itemTable',
    values: { name: entry.name, parent: entry.parent_name ?? '' },
  }),
  field: (entry) => ({
    key: 'trash.itemField',
    values: { name: entry.name, parent: entry.parent_name ?? '' },
  }),
  view: (entry) => ({
    key: 'trash.itemView',
    values: { name: entry.name, parent: entry.parent_name ?? '' },
  }),
  widget: (entry) => ({ key: 'trash.itemWidget', values: { name: entry.name } }),
  row: (entry) => ({
    key: 'trash.itemRow',
    values: { name: entry.name, parent: entry.parent_name ?? '' },
  }),
  rows: (entry) => ({
    key: 'trash.itemRows',
    values: { count: entry.names?.length ?? 0, parent: entry.parent_name ?? '' },
  }),
} satisfies Record<TrashItemType, Describer>

export function describeTrashEntry(entry: TrashEntry): TrashDescription {
  const describe = (DESCRIBERS as Partial<Record<string, Describer>>)[
    entry.trash_item_type
  ]

  // Named rather than hidden, for the reason an unrecognised notification is:
  // this is a list of things that can be restored, and one the build cannot name
  // is still one the user may want back.
  if (describe === undefined) {
    return { key: 'trash.itemUnknown', values: { name: entry.name } }
  }
  return describe(entry)
}

/**
 * Whether this entry can be restored on its own.
 *
 * A child cannot come back while its parent is still in the trash -- the backend
 * refuses with `ERROR_CANNOT_RESTORE_PARENT_BEFORE_CHILD` -- so the control is
 * disabled rather than offered and failed. Restoring the parent is what brings
 * the children back with it.
 */
export function isRestorable(
  entry: TrashEntry,
  trashed: { workspace: boolean; application: boolean },
): boolean {
  if (entry.trash_item_type === 'workspace') return true
  if (trashed.workspace) return false
  if (entry.trash_item_type === 'application') return true
  return !trashed.application
}
